import type { OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, inject, Input } from '@angular/core';
import { isNullOrUndefined } from 'is-what';
import moment from 'moment-timezone';
import { StringConstants } from '../../rcr-exchange/constants/string-constants';
import { UserService } from '../../shared/services/user.service';
import { DisclosureAnalyticsService } from '../services/disclosure-analytics.service';
import { PassDataService } from '../services/pass-data.service';

@Component({
  standalone: false,
  selector: 'invd-collateral-analyzer-statistics',
  templateUrl: './collateral-analyzer-statistics.component.html',
  styleUrls: ['./collateral-analyzer-statistics.component.scss'],
})
export class CollateralAnalyzerStatisticsComponent implements OnInit {
  private passDataService = inject(PassDataService);
  private userService = inject(UserService);
  private disclosureAnalyticsService = inject(DisclosureAnalyticsService);
  private cdr = inject(ChangeDetectorRef);

  @Input() loanPurposeRes: any;
  @Input() propertyTypeRes: any;
  @Input() channelRes: any;
  @Input() occupancyStatusRes: any;
  @Input() numberOfUnitsRes: any;
  @Input() originationYearRes: any;
  @Input() stateRes: any;
  @Input() servicersRes: any;
  @Input() borrowersRes: any;
  @Input() mortgageInsCancelIndRes: any;
  @Input() mortgageInsCoverageRes: any;

  @Input() mortgageLoanAmountQuartileRes: any;
  @Input() interestRateQuartileRes: any;
  @Input() netInterestRateCurrentQuartileRes: any;
  @Input() loanTermQuartileRes: any;
  @Input() loanAgeQuartileRes: any;
  @Input() loanToValueQuartileRes: any;
  @Input() combinedLoanToValueQuartileRes: any;
  @Input() borrowerCreditScoreQuartileRes: any;
  @Input() remainingMonthsToMaturityQuartileRes: any;

  public quartileRes: any;
  public month = '';
  public year = '';

  public loanPurposeResResolved: any;
  public propertyTypeResResolved: any;
  public channelResResolved: any;
  public occupancyStatusResResolved: any;
  public numberOfUnitsResResolved: any;
  public originationYearResResolved: any;
  public stateResResolved: any;
  public servicersResResolved: any;
  public borrowersResResolved: any;
  public mortgageInsCancelIndResResolved: any;
  public mortgageInsCoverageResResolved: any;

  public mortgageLoanAmountQuartileResResolved: any;
  public interestRateQuartileResResolved: any;
  public netInterestRateCurrentQuartileResResolved: any;
  public loanTermQuartileResResolved: any;
  public loanAgeQuartileResResolved: any;
  public loanToValueQuartileResResolved: any;
  public combinedLoanToValueQuartileResResolved: any;
  public borrowerCreditScoreQuartileResResolved: any;
  public remainingMonthsToMaturityQuartileResResolved: any;

  public loanPurposeResError = false;
  public propertyTypeResError = false;
  public channelResError = false;
  public occupancyStatusResError = false;
  public numberOfUnitsResError = false;
  public originationYearResError = false;
  public stateResError = false;
  public servicersResError = false;
  public borrowersResError = false;
  public mortgageInsCancelIndResError = false;
  public mortgageInsCoverageResError = false;

  /*
   * Quartile Error
   */
  public mortgageLoanAmountQuartileError = false;
  public interestRateQuartileError = false;
  public netInterestRateCurrentQuartileError = false;
  public loanTermQuartileError = false;
  public loanAgeQuartileError = false;
  public loanToValueQuartileError = false;
  public combinedLoanToValueQuartileError = false;
  public borrowerCreditScoreQuartileError = false;
  public remainingMonthsToMaturityQuartileError = false;

  public displayErrorBanner = false;
  public showBannerErrorMsg = '';
  public isJumpToDropdownOpen = false;

  public preliminaryDisclosureText: string = StringConstants.ANALYZER_PRELIMINARY_DISCLOSURE_TEXT;
  public disclosureStatisticsShowBanner: string[] = [];

  ngOnInit() {
    this.disclosureStatisticsShowBanner = [];
    this.checkAnalytics();
    this.resetStatisticsAttributes();
    if (this.passDataService.getGoBackEditFlag() === 'true') {
      const loan = this.passDataService.getLoanPurposeRes();
      const propertyType = this.passDataService.getPropertyTypeRes();
      const channel = this.passDataService.getChannelRes();
      const occupancyStatus = this.passDataService.getoccupancyStatusRes();
      const state = this.passDataService.getStateRes();
      const numberOfUnits = this.passDataService.getNumberOfUnitsRes();
      const mortgageInsCoverage = this.passDataService.getMortgageInsCoverageRes();
      const servicers = this.passDataService.getServicersRes();
      const originationYear = this.passDataService.getOriginationYearRes();
      const mortgageInsCancelInd = this.passDataService.getMortgageInsCancelIndRes();

      this.propertyTypeDetailsResolved(propertyType);
      this.channelDetailsResolved(channel);
      this.occupancyStatusDetailsResolved(occupancyStatus);
      this.numberOfUnitsDetailsResolved(numberOfUnits);
      this.stateDetailsResolved(state);
      this.mortgageInsCoverageDetailsResolved(mortgageInsCoverage);
      this.servicersDetailsResolved(servicers);
      this.originationYearDetailsResolved(originationYear);
      this.borrowersDetailsResolved(this.borrowersResResolved);
      this.mortgageInsCancelIndDetailsResolved(mortgageInsCancelInd);
      this.loanPurposeDetailsResolved(loan);
      this.showQuartileResResolved();
    } else {
      this.showBannerErrorMsg = '';
      this.displayErrorBanner = false;
      const downloadedTime = moment(new Date());
      this.month = downloadedTime.format('MM');
      this.year = downloadedTime.format('YYYY');

      this.preliminaryDisclosureText = StringConstants.ANALYZER_PRELIMINARY_DISCLOSURE_TEXT;

      this.loanPurposeResResolved = !isNullOrUndefined(this.loanPurposeRes)
        ? this.loanPurposeRes.loanPurpose
        : null;
      this.propertyTypeResResolved = this.propertyTypeRes && this.propertyTypeRes.propertyType;
      this.channelResResolved = this.channelRes && this.channelRes.channel;
      this.occupancyStatusResResolved =
        this.occupancyStatusRes && this.occupancyStatusRes.occupancyStatus;
      this.numberOfUnitsResResolved = this.numberOfUnitsRes && this.numberOfUnitsRes.numberOfUnits;
      this.stateResResolved = this.stateRes && this.stateRes.state;
      this.mortgageInsCoverageResResolved =
        this.mortgageInsCoverageRes && this.mortgageInsCoverageRes.mortgageInsCoverage;
      this.servicersResResolved = this.servicersRes && this.servicersRes.servicers;
      this.originationYearResResolved =
        this.originationYearRes && this.originationYearRes.originationYear;
      this.borrowersResResolved = this.borrowersRes && this.borrowersRes.borrowers;
      this.mortgageInsCancelIndResResolved =
        this.mortgageInsCancelIndRes && this.mortgageInsCancelIndRes.mortgageInsCancelInd;

      this.propertyTypeDetails(this.propertyTypeResResolved);
      this.channelDetails(this.channelResResolved);
      this.occupancyStatusDetails(this.occupancyStatusResResolved);
      this.numberOfUnitsDetails(this.numberOfUnitsResResolved);
      this.stateDetails(this.stateResResolved);
      this.mortgageInsCoverageDetails(this.mortgageInsCoverageResResolved);
      this.servicersDetails(this.servicersResResolved);
      this.originationYearDetails(this.originationYearResResolved);
      this.borrowersDetails(this.borrowersResResolved);
      this.mortgageInsCancelIndDetails(this.mortgageInsCancelIndResResolved);
      this.loanPurposeDetails(this.loanPurposeResResolved);
      this.showQuartile();
    }

    this.handleLoanPurposeError('error');
    this.handleOccupancyStatusError('error');
    this.handleChannelError('error');
    this.handleStateError('error');
    this.handlePropertyTypeError('error');
    this.handleBorrowerError('error');
    this.handleInsuranceCancelIndError('error');
    this.handleInsuranceCoverageeError('error');
    this.handleUnitsError('error');
    this.handleOriginationYearError('error');
    this.handleServicerDistributionError('error');

    this.handleQuartileMortgageLoanAmountError('error');
    this.handleQuartileInterestRateError('error');
    this.handleQuartileNetInterestRateError('error');
    this.handleQuartileLoanTermError('error');
    this.handleQuartileLoanAgeError('error');
    this.handleQuartileLoanToValueError('error');
    this.handleQuartileCombinedLoanToValueError('error');
    this.handleQuartileBorrowerCreditScoreError('error');
    this.handleQuartileRemainingMonthsToMaturityError('error');
  }

  public loanPurposeDetails(loan: any): any {
    this.loanPurposeAttributeMapping(loan);
  }

  public loanPurposeDetailsResolved(loan: any): any {
    loan = this.passDataService.getLoanPurposeRes();

    this.loanPurposeAttributeMapping(loan);
  }

  public loanPurposeAttributeMapping(loan): any {
    if (loan) {
      loan.forEach((item) => {
        switch (item.recTypeValue) {
          case StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_CODE_REFINANCE_CASH_OUT:
            item.attributeType =
              StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_LABEL_REFINANCE_CASH_OUT;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_CODE_REFINANCE_NO_CASH_OUT:
            item.attributeType =
              StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_LABEL_REFINANCE_NO_CASH_OUT;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_CODE_REFINANCE_NOT_SPECIFIED:
            item.attributeType =
              StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_LABEL_REFINANCE_NOT_SPECIFIED;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_CODE_PURCHASE:
            item.attributeType = StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_LABEL_PURCHASE;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_CODE_MODIFIED_LOSS_MITIGATION:
            item.attributeType =
              StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_LABEL_MODIFIED_LOSS_MITIGATION;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_CODE_ERRORCODE:
            item.attributeType = StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_LABEL_ERRORCODE;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_CODE_NOT_AVAILABLE:
            item.attributeType =
              StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_LABEL_NOT_AVAILABLE;
            break;
          default:
            item.attributeType = '-';
            break;
        }
      });
    }

    this.loanPurposeResResolved = loan;
  }

  public propertyTypeDetails(propertyType: any): any {
    this.propertyTypeAttributeMapping(propertyType);
  }

  public propertyTypeDetailsResolved(propertyType: any): any {
    propertyType = this.passDataService.getPropertyTypeRes();
    this.propertyTypeAttributeMapping(propertyType);
  }

  public propertyTypeAttributeMapping(propertyType): any {
    if (propertyType) {
      propertyType.forEach((item) => {
        switch (item.recTypeValue) {
          case StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_CODE_COOPERATIVE:
            item.attributeType =
              StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_LABEL_COOPERATIVE;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_CODE_CONDOMINIUM:
            item.attributeType =
              StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_LABEL_CONDOMINIUM;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_CODE_PLANNED_UNIT:
            item.attributeType =
              StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_LABEL_PLANNED_UNIT;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_CODE_SINGLE_FAMILY:
            item.attributeType =
              StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_LABEL_SINGLE_FAMILY;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_CODE_MANUFACTURED_HOUSING:
            item.attributeType =
              StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_LABEL_MANUFACTURED_HOUSING;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_CODE_ERRORCODE:
            item.attributeType = StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_LABEL_ERRORCODE;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_CODE_NOT_AVAILABLE:
            item.attributeType =
              StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_LABEL_NOT_AVAILABLE;
            break;
          default:
            item.attributeType = '-';
            break;
        }
      });
    }

    this.propertyTypeResResolved = propertyType;
  }

  public channelDetails(channel: any): any {
    this.channelDetailsAttributeMapping(channel);
  }

  public channelDetailsResolved(channel: any): any {
    channel = this.passDataService.getChannelRes();

    this.channelDetailsAttributeMapping(channel);
  }

  public channelDetailsAttributeMapping(channel): any {
    if (channel) {
      channel.forEach((item) => {
        switch (item.recTypeValue) {
          case StringConstants.ANALYZER_DISCLOSURE_CHANNEL_CODE_RETAIL:
            item.attributeType = StringConstants.ANALYZER_DISCLOSURE_CHANNEL_LABEL_RETAIL;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_CHANNEL_CODE_BROKER:
            item.attributeType = StringConstants.ANALYZER_DISCLOSURE_CHANNEL_LABEL_BROKER;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_CHANNEL_CODE_CORRESPONDENT:
            item.attributeType = StringConstants.ANALYZER_DISCLOSURE_CHANNEL_LABEL_CORRESPONDENT;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_CHANNEL_CODE_THIRD_PARTY_ORIGINATION:
            item.attributeType =
              StringConstants.ANALYZER_DISCLOSURE_CHANNEL_LABEL_THIRD_PARTY_ORIGINATION;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_CHANNEL_CODE_ERRORCODE:
            item.attributeType = StringConstants.ANALYZER_DISCLOSURE_CHANNEL_LABEL_ERRORCODE;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_CHANNEL_CODE_NOT_AVAILABLE:
            item.attributeType = StringConstants.ANALYZER_DISCLOSURE_CHANNEL_LABEL_NOT_AVAILABLE;
            break;
          default:
            item.attributeType = '-';
            break;
        }
      });
    }

    this.channelResResolved = channel;
  }

  public occupancyStatusDetails(occupancyStatus: any): any {
    this.occupancyStatusAttributeMapping(occupancyStatus);
  }

  public occupancyStatusDetailsResolved(occupancyStatus: any): any {
    occupancyStatus = this.passDataService.getoccupancyStatusRes();

    this.occupancyStatusAttributeMapping(occupancyStatus);
  }

  public occupancyStatusAttributeMapping(occupancyStatus): void {
    if (occupancyStatus) {
      occupancyStatus.forEach((item) => {
        switch (item.recTypeValue) {
          case StringConstants.ANALYZER_DISCLOSURE_OCCUPANCY_STATUS_CODE_PRIMARY_HOME:
            item.attributeType =
              StringConstants.ANALYZER_DISCLOSURE_OCCUPANCY_STATUS_LABEL_PRIMARY_HOME;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_OCCUPANCY_STATUS_CODE_SECONDARY_HOME:
            item.attributeType =
              StringConstants.ANALYZER_DISCLOSURE_OCCUPANCY_STATUS_LABEL_SECONDARY_HOME;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_OCCUPANCY_STATUS_CODE_INVESTMENT_PROPERTY:
            item.attributeType =
              StringConstants.ANALYZER_DISCLOSURE_OCCUPANCY_STATUS_LABEL_INVESTMENT_PROPERTY;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_OCCUPANCY_STATUS_CODE_ERRORCODE:
            item.attributeType =
              StringConstants.ANALYZER_DISCLOSURE_OCCUPANCY_STATUS_LABEL_ERRORCODE;
            break;
          case StringConstants.ANALYZER_DISCLOSURE_OCCUPANCY_STATUS_CODE_NOT_AVAILABLE:
            item.attributeType =
              StringConstants.ANALYZER_DISCLOSURE_OCCUPANCY_STATUS_LABEL_NOT_AVAILABLE;
            break;
          default:
            item.attributeType = '-';
            break;
        }
      });
    }

    this.occupancyStatusResResolved = occupancyStatus;
  }

  public stateDetails(state: any): any {
    this.stateResResolved = state;
  }

  public stateDetailsResolved(state: any): any {
    state = this.passDataService.getStateRes();

    this.stateResResolved = state;
  }

  public numberOfUnitsDetails(numberOfUnits: any): any {
    this.numberOfUnitsResResolved = numberOfUnits;
  }

  public numberOfUnitsDetailsResolved(numberOfUnits: any): any {
    numberOfUnits = this.passDataService.getNumberOfUnitsRes();

    this.numberOfUnitsResResolved = numberOfUnits;
  }

  public mortgageInsCoverageDetails(mortgageInsCoverage: any): any {
    this.mortgageInsCoverageResResolved = mortgageInsCoverage;
  }

  public mortgageInsCoverageDetailsResolved(mortgageInsCoverage: any): any {
    mortgageInsCoverage = this.passDataService.getMortgageInsCoverageRes();

    this.mortgageInsCoverageResResolved = mortgageInsCoverage;
  }

  public servicersDetails(servicers: any): any {
    this.servicersResResolved = this.servicerLessThanOnePercent(servicers);
  }

  public servicersDetailsResolved(servicers: any): any {
    servicers = this.passDataService.getServicersRes();

    this.servicersResResolved = this.servicerLessThanOnePercent(servicers);
  }

  public originationYearDetails(originationYear: any): any {
    this.originationYearResResolved = originationYear;
  }

  public originationYearDetailsResolved(originationYear: any): any {
    originationYear = this.passDataService.getOriginationYearRes();

    this.originationYearResResolved = originationYear;
  }

  public borrowersDetails(borrowers: any): any {
    this.borrowersResResolved = borrowers;
  }

  public borrowersDetailsResolved(borrowers: any): any {
    borrowers = this.passDataService.getBorrowersRes();

    this.borrowersResResolved = borrowers;
  }

  public mortgageInsCancelIndDetails(mortgageInsCancelInd: any): any {
    this.mortgageInsCancelIndResResolved = mortgageInsCancelInd;
  }

  public mortgageInsCancelIndDetailsResolved(mortgageInsCancelInd: any): any {
    mortgageInsCancelInd = this.passDataService.getMortgageInsCancelIndRes();

    this.mortgageInsCancelIndResResolved = mortgageInsCancelInd;
  }

  public setSelected(_selection): void {
    this.isJumpToDropdownOpen = false;
  }

  public toggleJumpToDropdown(): void {
    this.isJumpToDropdownOpen = !this.isJumpToDropdownOpen;
  }

  public closeJumpToDropdown(): void {
    this.isJumpToDropdownOpen = false;
  }

  public showQuartile(): void {
    this.mortgageLoanAmountQuartileResResolved = this.mortgageLoanAmountQuartileRes;
    this.interestRateQuartileResResolved = this.interestRateQuartileRes;
    this.netInterestRateCurrentQuartileResResolved = this.netInterestRateCurrentQuartileRes;
    this.loanTermQuartileResResolved = this.loanTermQuartileRes;
    this.loanAgeQuartileResResolved = this.loanAgeQuartileRes;
    this.loanToValueQuartileResResolved = this.loanToValueQuartileRes;
    this.combinedLoanToValueQuartileResResolved = this.combinedLoanToValueQuartileRes;
    this.borrowerCreditScoreQuartileResResolved = this.borrowerCreditScoreQuartileRes;
    this.remainingMonthsToMaturityQuartileResResolved = this.remainingMonthsToMaturityQuartileRes;

    this.frameQuartileResponse();
  }

  public showQuartileResResolved(): void {
    this.mortgageLoanAmountQuartileResResolved =
      this.passDataService.getMortgageLoanAmountQuartileRes();
    this.interestRateQuartileResResolved = this.passDataService.getInterestRateQuartileRes();
    this.netInterestRateCurrentQuartileResResolved =
      this.passDataService.getNetInterestRateCurrentQuartileRes();
    this.loanTermQuartileResResolved = this.passDataService.getLoanTermQuartileRes();
    this.loanAgeQuartileResResolved = this.passDataService.getloanAgeQuartileRes();
    this.loanToValueQuartileResResolved = this.passDataService.getLoanToValueQuartileRes();
    this.combinedLoanToValueQuartileResResolved =
      this.passDataService.getCombinedLoanToValueQuartileRes();
    this.borrowerCreditScoreQuartileResResolved =
      this.passDataService.getBorrowerCreditScoreQuartileRes();
    this.remainingMonthsToMaturityQuartileResResolved =
      this.passDataService.getRemainingMonthsToMaturityQuartileRes();
    this.frameQuartileResponse();
  }

  public resetStatisticsAttributes(): void {
    this.loanPurposeResResolved = null;
    this.propertyTypeResResolved = null;
    this.channelResResolved = null;
    this.occupancyStatusResResolved = null;
    this.numberOfUnitsResResolved = null;
    this.originationYearResResolved = null;
    this.stateResResolved = null;
    this.servicersResResolved = null;
    this.borrowersResResolved = null;
    this.mortgageInsCancelIndResResolved = null;
    this.mortgageInsCoverageResResolved = null;

    this.mortgageLoanAmountQuartileResResolved = null;
    this.interestRateQuartileResResolved = null;
    this.netInterestRateCurrentQuartileResResolved = null;
    this.loanTermQuartileResResolved = null;
    this.loanAgeQuartileResResolved = null;
    this.loanToValueQuartileResResolved = null;
    this.combinedLoanToValueQuartileResResolved = null;
    this.borrowerCreditScoreQuartileResResolved = null;
    this.remainingMonthsToMaturityQuartileResResolved = null;
  }

  public frameQuartileResponse(): void {
    const mBundlemaximum: any = {};
    const mBundlemedian: any = {};
    const mBundleminimum: any = {};
    const mBundleq25pct: any = {};
    const mBundleq75pct: any = {};
    this.quartileRes = [];

    mBundlemaximum.attribute = 'Maximum';
    mBundlemaximum.mortgageLoanAmount =
      this.mortgageLoanAmountQuartileResResolved &&
      this.mortgageLoanAmountQuartileResResolved.mortgageLoanAmount &&
      this.mortgageLoanAmountQuartileResResolved.mortgageLoanAmount[0].maximum;
    mBundlemaximum.interestRate =
      this.interestRateQuartileResResolved &&
      this.interestRateQuartileResResolved.interestRate &&
      this.interestRateQuartileResResolved.interestRate[0].maximum;
    mBundlemaximum.netInterestRate_Current =
      this.netInterestRateCurrentQuartileResResolved &&
      this.netInterestRateCurrentQuartileResResolved.netInterestRate_Current &&
      this.netInterestRateCurrentQuartileResResolved.netInterestRate_Current[0].maximum;
    mBundlemaximum.loanTerm =
      this.loanTermQuartileResResolved &&
      this.loanTermQuartileResResolved.loanTerm &&
      parseInt(this.loanTermQuartileResResolved.loanTerm[0].maximum, 10);
    mBundlemaximum.loanAge =
      this.loanAgeQuartileResResolved &&
      this.loanAgeQuartileResResolved.loanAge &&
      parseInt(this.loanAgeQuartileResResolved.loanAge[0].maximum, 10);
    mBundlemaximum.loanToValue =
      this.loanToValueQuartileResResolved &&
      this.loanToValueQuartileResResolved.loanToValue &&
      parseInt(this.loanToValueQuartileResResolved.loanToValue[0].maximum, 10);
    mBundlemaximum.combined_LoanToValue =
      this.combinedLoanToValueQuartileResResolved &&
      this.combinedLoanToValueQuartileResResolved.combined_LoanToValue &&
      parseInt(this.combinedLoanToValueQuartileResResolved.combined_LoanToValue[0].maximum, 10);
    mBundlemaximum.borrowerCreditScore =
      this.borrowerCreditScoreQuartileResResolved &&
      this.borrowerCreditScoreQuartileResResolved.borrowerCreditScore &&
      parseInt(this.borrowerCreditScoreQuartileResResolved.borrowerCreditScore[0].maximum, 10);
    mBundlemaximum.remainingMonthsToMaturity =
      this.remainingMonthsToMaturityQuartileResResolved &&
      this.remainingMonthsToMaturityQuartileResResolved.remainingMonthsToMaturity &&
      parseInt(
        this.remainingMonthsToMaturityQuartileResResolved.remainingMonthsToMaturity[0].maximum,
        10,
      );
    mBundlemaximum.DTI = '-';

    mBundleq75pct.attribute = '75%';
    mBundleq75pct.mortgageLoanAmount =
      this.mortgageLoanAmountQuartileResResolved &&
      this.mortgageLoanAmountQuartileResResolved.mortgageLoanAmount &&
      this.mortgageLoanAmountQuartileResResolved.mortgageLoanAmount[0].q75pct;
    mBundleq75pct.interestRate =
      this.interestRateQuartileResResolved &&
      this.interestRateQuartileResResolved.interestRate &&
      this.interestRateQuartileResResolved.interestRate[0].q75pct;
    mBundleq75pct.netInterestRate_Current =
      this.netInterestRateCurrentQuartileResResolved &&
      this.netInterestRateCurrentQuartileResResolved.netInterestRate_Current &&
      this.netInterestRateCurrentQuartileResResolved.netInterestRate_Current[0].q75pct;
    mBundleq75pct.loanTerm =
      this.loanTermQuartileResResolved &&
      this.loanTermQuartileResResolved.loanTerm &&
      parseInt(this.loanTermQuartileResResolved.loanTerm[0].q75pct, 10);
    mBundleq75pct.loanAge =
      this.loanAgeQuartileResResolved &&
      this.loanAgeQuartileResResolved.loanAge &&
      parseInt(this.loanAgeQuartileResResolved.loanAge[0].q75pct, 10);
    mBundleq75pct.loanToValue =
      this.loanToValueQuartileResResolved &&
      this.loanToValueQuartileResResolved.loanToValue &&
      parseInt(this.loanToValueQuartileResResolved.loanToValue[0].q75pct, 10);
    mBundleq75pct.combined_LoanToValue =
      this.combinedLoanToValueQuartileResResolved &&
      this.combinedLoanToValueQuartileResResolved.combined_LoanToValue &&
      parseInt(this.combinedLoanToValueQuartileResResolved.combined_LoanToValue[0].q75pct, 10);
    mBundleq75pct.borrowerCreditScore =
      this.borrowerCreditScoreQuartileResResolved &&
      this.borrowerCreditScoreQuartileResResolved.borrowerCreditScore &&
      parseInt(this.borrowerCreditScoreQuartileResResolved.borrowerCreditScore[0].q75pct, 10);
    mBundleq75pct.remainingMonthsToMaturity =
      this.remainingMonthsToMaturityQuartileResResolved &&
      this.remainingMonthsToMaturityQuartileResResolved.remainingMonthsToMaturity &&
      parseInt(
        this.remainingMonthsToMaturityQuartileResResolved.remainingMonthsToMaturity[0].q75pct,
        10,
      );
    mBundleq75pct.DTI = '-';

    mBundlemedian.attribute = 'Median';
    mBundlemedian.mortgageLoanAmount =
      this.mortgageLoanAmountQuartileResResolved &&
      this.mortgageLoanAmountQuartileResResolved.mortgageLoanAmount &&
      this.mortgageLoanAmountQuartileResResolved.mortgageLoanAmount[0].median;
    mBundlemedian.interestRate =
      this.interestRateQuartileResResolved &&
      this.interestRateQuartileResResolved.interestRate &&
      this.interestRateQuartileResResolved.interestRate[0].median;
    mBundlemedian.netInterestRate_Current =
      this.netInterestRateCurrentQuartileResResolved &&
      this.netInterestRateCurrentQuartileResResolved.netInterestRate_Current &&
      this.netInterestRateCurrentQuartileResResolved.netInterestRate_Current[0].median;
    mBundlemedian.loanTerm =
      this.loanTermQuartileResResolved &&
      this.loanTermQuartileResResolved.loanTerm &&
      parseInt(this.loanTermQuartileResResolved.loanTerm[0].median, 10);
    mBundlemedian.loanAge =
      this.loanAgeQuartileResResolved &&
      this.loanAgeQuartileResResolved.loanAge &&
      parseInt(this.loanAgeQuartileResResolved.loanAge[0].median, 10);
    mBundlemedian.loanToValue =
      this.loanToValueQuartileResResolved &&
      this.loanToValueQuartileResResolved.loanToValue &&
      parseInt(this.loanToValueQuartileResResolved.loanToValue[0].median, 10);
    mBundlemedian.combined_LoanToValue =
      this.combinedLoanToValueQuartileResResolved &&
      this.combinedLoanToValueQuartileResResolved.combined_LoanToValue &&
      parseInt(this.combinedLoanToValueQuartileResResolved.combined_LoanToValue[0].median, 10);
    mBundlemedian.borrowerCreditScore =
      this.borrowerCreditScoreQuartileResResolved &&
      this.borrowerCreditScoreQuartileResResolved.borrowerCreditScore &&
      parseInt(this.borrowerCreditScoreQuartileResResolved.borrowerCreditScore[0].median, 10);
    mBundlemedian.remainingMonthsToMaturity =
      this.remainingMonthsToMaturityQuartileResResolved &&
      this.remainingMonthsToMaturityQuartileResResolved.remainingMonthsToMaturity &&
      parseInt(
        this.remainingMonthsToMaturityQuartileResResolved.remainingMonthsToMaturity[0].median,
        10,
      );
    mBundlemedian.DTI = '-';

    mBundleq25pct.attribute = '25%';
    mBundleq25pct.mortgageLoanAmount =
      this.mortgageLoanAmountQuartileResResolved &&
      this.mortgageLoanAmountQuartileResResolved.mortgageLoanAmount &&
      this.mortgageLoanAmountQuartileResResolved.mortgageLoanAmount[0].q25pct;
    mBundleq25pct.interestRate =
      this.interestRateQuartileResResolved &&
      this.interestRateQuartileResResolved.interestRate &&
      this.interestRateQuartileResResolved.interestRate[0].q25pct;
    mBundleq25pct.netInterestRate_Current =
      this.netInterestRateCurrentQuartileResResolved &&
      this.netInterestRateCurrentQuartileResResolved.netInterestRate_Current &&
      this.netInterestRateCurrentQuartileResResolved.netInterestRate_Current[0].q25pct;
    mBundleq25pct.loanTerm =
      this.loanTermQuartileResResolved &&
      this.loanTermQuartileResResolved.loanTerm &&
      parseInt(this.loanTermQuartileResResolved.loanTerm[0].q25pct, 10);
    mBundleq25pct.loanAge =
      this.loanAgeQuartileResResolved &&
      this.loanAgeQuartileResResolved.loanAge &&
      parseInt(this.loanAgeQuartileResResolved.loanAge[0].q25pct, 10);
    mBundleq25pct.loanToValue =
      this.loanToValueQuartileResResolved &&
      this.loanToValueQuartileResResolved.loanToValue &&
      parseInt(this.loanToValueQuartileResResolved.loanToValue[0].q25pct, 10);
    mBundleq25pct.combined_LoanToValue =
      this.combinedLoanToValueQuartileResResolved &&
      this.combinedLoanToValueQuartileResResolved.combined_LoanToValue &&
      parseInt(this.combinedLoanToValueQuartileResResolved.combined_LoanToValue[0].q25pct, 10);
    mBundleq25pct.borrowerCreditScore =
      this.borrowerCreditScoreQuartileResResolved &&
      this.borrowerCreditScoreQuartileResResolved.borrowerCreditScore &&
      parseInt(this.borrowerCreditScoreQuartileResResolved.borrowerCreditScore[0].q25pct, 10);
    mBundleq25pct.remainingMonthsToMaturity =
      this.remainingMonthsToMaturityQuartileResResolved &&
      this.remainingMonthsToMaturityQuartileResResolved.remainingMonthsToMaturity &&
      parseInt(
        this.remainingMonthsToMaturityQuartileResResolved.remainingMonthsToMaturity[0].q25pct,
        10,
      );
    mBundleq25pct.DTI = '-';

    mBundleminimum.attribute = 'Minimum';
    mBundleminimum.mortgageLoanAmount =
      this.mortgageLoanAmountQuartileResResolved &&
      this.mortgageLoanAmountQuartileResResolved.mortgageLoanAmount &&
      this.mortgageLoanAmountQuartileResResolved.mortgageLoanAmount[0].minimum;
    mBundleminimum.interestRate =
      this.interestRateQuartileResResolved &&
      this.interestRateQuartileResResolved.interestRate &&
      this.interestRateQuartileResResolved.interestRate[0].minimum;
    mBundleminimum.netInterestRate_Current =
      this.netInterestRateCurrentQuartileResResolved &&
      this.netInterestRateCurrentQuartileResResolved.netInterestRate_Current &&
      this.netInterestRateCurrentQuartileResResolved.netInterestRate_Current[0].minimum;
    mBundleminimum.loanTerm =
      this.loanTermQuartileResResolved &&
      this.loanTermQuartileResResolved.loanTerm &&
      parseInt(this.loanTermQuartileResResolved.loanTerm[0].minimum, 10);
    mBundleminimum.loanAge =
      this.loanAgeQuartileResResolved &&
      this.loanAgeQuartileResResolved.loanAge &&
      parseInt(this.loanAgeQuartileResResolved.loanAge[0].minimum, 10);
    mBundleminimum.loanToValue =
      this.loanToValueQuartileResResolved &&
      this.loanToValueQuartileResResolved.loanToValue &&
      parseInt(this.loanToValueQuartileResResolved.loanToValue[0].minimum, 10);
    mBundleminimum.combined_LoanToValue =
      this.combinedLoanToValueQuartileResResolved &&
      this.combinedLoanToValueQuartileResResolved.combined_LoanToValue &&
      parseInt(this.combinedLoanToValueQuartileResResolved.combined_LoanToValue[0].minimum, 10);
    mBundleminimum.borrowerCreditScore =
      this.borrowerCreditScoreQuartileResResolved &&
      this.borrowerCreditScoreQuartileResResolved.borrowerCreditScore &&
      parseInt(this.borrowerCreditScoreQuartileResResolved.borrowerCreditScore[0].minimum, 10);
    mBundleminimum.remainingMonthsToMaturity =
      this.remainingMonthsToMaturityQuartileResResolved &&
      this.remainingMonthsToMaturityQuartileResResolved.remainingMonthsToMaturity &&
      parseInt(
        this.remainingMonthsToMaturityQuartileResResolved.remainingMonthsToMaturity[0].minimum,
        10,
      );
    mBundleminimum.DTI = '-';

    this.quartileRes.push(mBundlemaximum);
    this.quartileRes.push(mBundleq75pct);
    this.quartileRes.push(mBundlemedian);
    this.quartileRes.push(mBundleq25pct);
    this.quartileRes.push(mBundleminimum);

    this.passDataService.setQuartileCompleteRes(this.quartileRes);
  }

  public scrollToTop(): void {
    window.scrollTo(0, 90);
  }

  public servicerLessThanOnePercent(servicers): any {
    const servicerResResolved = [];
    const mBundleLessThanOnePercent: any = {};
    let aggUPBLessThan1Percent = 0;
    let loanCountLessThan1Percent = 0;
    let pctAggUPBLessThan1Percent = 0;
    let pctLoanCountLessThan1Percent = 0;

    if (servicers) {
      servicers.forEach((item) => {
        const mBundle: any = {};

        if (item.pctAggUPB < 1) {
          aggUPBLessThan1Percent = aggUPBLessThan1Percent + parseFloat(item.aggUPB);
          loanCountLessThan1Percent = loanCountLessThan1Percent + parseFloat(item.loanCount);
          pctAggUPBLessThan1Percent = pctAggUPBLessThan1Percent + parseFloat(item.pctAggUPB);
          pctLoanCountLessThan1Percent =
            pctLoanCountLessThan1Percent + parseFloat(item.pctLoanCount);
        } else {
          mBundle.recTypeValue = item.recTypeValue;
          mBundle.aggUPB = item.aggUPB;
          mBundle.loanCount = item.loanCount;
          mBundle.pctAggUPB = item.pctAggUPB;
          mBundle.pctLoanCount = item.pctLoanCount;
        }

        if (!isNullOrUndefined(mBundle.recTypeValue)) {
          servicerResResolved.push(mBundle);
        }
      });
    }

    mBundleLessThanOnePercent.recTypeValue = 'LESS THAN 1%';
    mBundleLessThanOnePercent.aggUPB = aggUPBLessThan1Percent;
    mBundleLessThanOnePercent.loanCount = loanCountLessThan1Percent;
    mBundleLessThanOnePercent.pctAggUPB =
      pctAggUPBLessThan1Percent && pctAggUPBLessThan1Percent.toFixed(2);
    mBundleLessThanOnePercent.pctLoanCount =
      pctLoanCountLessThan1Percent && pctLoanCountLessThan1Percent.toFixed(2);

    if (aggUPBLessThan1Percent && aggUPBLessThan1Percent !== 0) {
      servicerResResolved.push(mBundleLessThanOnePercent);
    }

    this.passDataService.setServicersLessThanOnePercentRes(servicerResResolved);
    return servicerResResolved;
  }

  /*
   * handle error scenario
   */

  public handleLoanPurposeError(_error: any): any {
    this.loanPurposeResError =
      this.passDataService.getAnalyzerStatisticsErrorIconVal() &&
      this.passDataService.getAnalyzerStatisticsErrorIconVal().includes('loanPurposeErrorDisplay')
        ? true
        : false;
    if (this.loanPurposeResError) {
      this.showErrorBanner('loanPurposeError');
    }
  }

  public handleOccupancyStatusError(_error: any): any {
    this.occupancyStatusResError =
      this.passDataService.getAnalyzerStatisticsErrorIconVal() &&
      this.passDataService
        .getAnalyzerStatisticsErrorIconVal()
        .includes('occupancyStatusErrorDisplay')
        ? true
        : false;
    if (this.occupancyStatusResError) {
      this.showErrorBanner('occupancyStatusErrorDisplay');
    }
  }

  public handleChannelError(_error: any): any {
    this.channelResError =
      this.passDataService.getAnalyzerStatisticsErrorIconVal() &&
      this.passDataService.getAnalyzerStatisticsErrorIconVal().includes('channelErrorDisplay')
        ? true
        : false;
    if (this.channelResError) {
      this.showErrorBanner('channelError');
    }
  }

  public handleStateError(_error: any): any {
    this.stateResError =
      this.passDataService.getAnalyzerStatisticsErrorIconVal() &&
      this.passDataService.getAnalyzerStatisticsErrorIconVal().includes('stateErrorDisplay')
        ? true
        : false;
    if (this.stateResError) {
      this.showErrorBanner('stateErrorDisplay');
    }
  }

  public handlePropertyTypeError(_error: any): any {
    this.propertyTypeResError =
      this.passDataService.getAnalyzerStatisticsErrorIconVal() &&
      this.passDataService.getAnalyzerStatisticsErrorIconVal().includes('propertyTypeErrorDisplay')
        ? true
        : false;
    if (this.propertyTypeResError) {
      this.showErrorBanner('propertyTypeErrorDisplay');
    }
  }

  public handleBorrowerError(_error: any): any {
    this.borrowersResError =
      this.passDataService.getAnalyzerStatisticsErrorIconVal() &&
      this.passDataService.getAnalyzerStatisticsErrorIconVal().includes('borrowerErrorDisplay')
        ? true
        : false;
    if (this.borrowersResError) {
      this.showErrorBanner('borrowerErrorDisplay');
    }
  }

  public handleInsuranceCancelIndError(_error: any): any {
    this.mortgageInsCancelIndResError =
      this.passDataService.getAnalyzerStatisticsErrorIconVal() &&
      this.passDataService
        .getAnalyzerStatisticsErrorIconVal()
        .includes('insuranceCancelIndErrorDisplay')
        ? true
        : false;
    if (this.mortgageInsCancelIndResError) {
      this.showErrorBanner('insuranceCancelIndErrorDisplay');
    }
  }

  public handleInsuranceCoverageeError(_error: any): any {
    this.mortgageInsCoverageResError =
      this.passDataService.getAnalyzerStatisticsErrorIconVal() &&
      this.passDataService
        .getAnalyzerStatisticsErrorIconVal()
        .includes('insuranceCoverageErrorDisplay')
        ? true
        : false;
    if (this.mortgageInsCoverageResError) {
      this.showErrorBanner('insuranceCoverageErrorDisplay');
    }
  }

  public handleUnitsError(_error: any): any {
    this.numberOfUnitsResError =
      this.passDataService.getAnalyzerStatisticsErrorIconVal() &&
      this.passDataService.getAnalyzerStatisticsErrorIconVal().includes('unitsErrorDisplay')
        ? true
        : false;
    if (this.mortgageInsCoverageResError) {
      this.showErrorBanner('unitsErrorDisplay');
    }
  }

  public handleOriginationYearError(_error: any): any {
    this.originationYearResError =
      this.passDataService.getAnalyzerStatisticsErrorIconVal() &&
      this.passDataService
        .getAnalyzerStatisticsErrorIconVal()
        .includes('originationYearErrorDisplay')
        ? true
        : false;
    if (this.originationYearResError) {
      this.showErrorBanner('originationYearErrorDisplay');
    }
  }

  public handleServicerDistributionError(_error: any): any {
    this.servicersResError =
      this.passDataService.getAnalyzerStatisticsErrorIconVal() &&
      this.passDataService.getAnalyzerStatisticsErrorIconVal().includes('servicerErrorDisplay')
        ? true
        : false;
    if (this.servicersResError) {
      this.showErrorBanner('servicerErrorDisplay');
    }
  }

  /*
   * Quartile error handling
   */

  public handleQuartileMortgageLoanAmountError(_error: any): any {
    this.mortgageLoanAmountQuartileError =
      this.passDataService.getAnalyzerStatisticsErrorIconVal() &&
      this.passDataService
        .getAnalyzerStatisticsErrorIconVal()
        .includes('quartileMortgageLoanAmountErrorDisplay')
        ? true
        : false;
    if (this.mortgageLoanAmountQuartileError) {
      this.showErrorBanner('quartileMortgageLoanAmountErrorDisplay');
    }
  }

  public handleQuartileInterestRateError(_error: any): any {
    this.interestRateQuartileError =
      this.passDataService.getAnalyzerStatisticsErrorIconVal() &&
      this.passDataService
        .getAnalyzerStatisticsErrorIconVal()
        .includes('quartileInterestRateErrorDisplay')
        ? true
        : false;
    if (this.interestRateQuartileError) {
      this.showErrorBanner('quartileInterestRateErrorDisplay');
    }
  }

  public handleQuartileNetInterestRateError(_error: any): any {
    this.netInterestRateCurrentQuartileError =
      this.passDataService.getAnalyzerStatisticsErrorIconVal() &&
      this.passDataService
        .getAnalyzerStatisticsErrorIconVal()
        .includes('quartileNetInterestRateErrorDisplay')
        ? true
        : false;
    if (this.netInterestRateCurrentQuartileError) {
      this.showErrorBanner('quartileNetInterestRateErrorDisplay');
    }
  }

  public handleQuartileLoanTermError(_error: any): any {
    this.loanTermQuartileError =
      this.passDataService.getAnalyzerStatisticsErrorIconVal() &&
      this.passDataService
        .getAnalyzerStatisticsErrorIconVal()
        .includes('quartileLoanTermErrorDisplay')
        ? true
        : false;
    if (this.loanTermQuartileError) {
      this.showErrorBanner('quartileLoanTermErrorDisplay');
    }
  }

  public handleQuartileLoanAgeError(_error: any): any {
    this.loanAgeQuartileError =
      this.passDataService.getAnalyzerStatisticsErrorIconVal() &&
      this.passDataService
        .getAnalyzerStatisticsErrorIconVal()
        .includes('quartileLoanAgeErrorDisplay')
        ? true
        : false;
    if (this.loanAgeQuartileError) {
      this.showErrorBanner('quartileLoanAgeErrorDisplay');
    }
  }

  public handleQuartileLoanToValueError(_error: any): any {
    this.loanToValueQuartileError =
      this.passDataService.getAnalyzerStatisticsErrorIconVal() &&
      this.passDataService
        .getAnalyzerStatisticsErrorIconVal()
        .includes('quartileLoanToValueErrorDisplay')
        ? true
        : false;
    if (this.loanToValueQuartileError) {
      this.showErrorBanner('quartileLoanToValueErrorDisplay');
    }
  }

  public handleQuartileCombinedLoanToValueError(_error: any): any {
    this.combinedLoanToValueQuartileError =
      this.passDataService.getAnalyzerStatisticsErrorIconVal() &&
      this.passDataService
        .getAnalyzerStatisticsErrorIconVal()
        .includes('quartileCombinedLoanToValueErrorDisplay')
        ? true
        : false;
    if (this.combinedLoanToValueQuartileError) {
      this.showErrorBanner('quartileCombinedLoanToValueErrorDisplay');
    }
  }

  public handleQuartileBorrowerCreditScoreError(_error: any): any {
    this.borrowerCreditScoreQuartileError =
      this.passDataService.getAnalyzerStatisticsErrorIconVal() &&
      this.passDataService
        .getAnalyzerStatisticsErrorIconVal()
        .includes('quartileBorrowerCreditScoreErrorDisplay')
        ? true
        : false;
    if (this.borrowerCreditScoreQuartileError) {
      this.showErrorBanner('quartileBorrowerCreditScoreErrorDisplay');
    }
  }

  public handleQuartileRemainingMonthsToMaturityError(_error: any): any {
    this.remainingMonthsToMaturityQuartileError =
      this.passDataService.getAnalyzerStatisticsErrorIconVal() &&
      this.passDataService
        .getAnalyzerStatisticsErrorIconVal()
        .includes('quartileRemainingMonthsToMaturityErrorDisplay')
        ? true
        : false;
    if (this.remainingMonthsToMaturityQuartileError) {
      this.showErrorBanner('quartileRemainingMonthsToMaturityErrorDisplay');
    }
  }

  public closeMessageBanner(): void {
    this.displayErrorBanner = false;
  }

  public showErrorBanner(val: string): void {
    this.disclosureStatisticsShowBanner.push(val);
    this.displayErrorBanner =
      this.disclosureStatisticsShowBanner && this.disclosureStatisticsShowBanner.length > 0
        ? true
        : false;
    this.showBannerErrorMsg = StringConstants.ANALYZER_DETAILS_ERROR_BANNER_MESSAGE;
  }

  public checkAnalytics(): void {
    const analyticsTab: any = {};
    analyticsTab.user = this.userService.getUserName();
    analyticsTab.tabName = 'disclosureStatistics';
    analyticsTab.tempMega = this.passDataService.getTempMega();
    this.disclosureAnalyticsService.checkDisclosureAnalytics(analyticsTab).subscribe({
      next: (data) => this.handleDisclosureAnalyticsSuccess(data),
      error: (error) => this.handleDisclosureAnalyticsServiceFail(error),
    });
  }

  public handleDisclosureAnalyticsSuccess(_res: any) {
    this.cdr.markForCheck();
  }

  public handleDisclosureAnalyticsServiceFail(_error: any): void {
    this.cdr.markForCheck();
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
